import { ApiError } from '@lib/api/api-response';
import { readApiErrorMessage } from '@lib/api/utils/read-api-error-message';
import {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
} from '@lib/insights/constants/accepted-file-types';
import { fileSizeLimitMessage } from '@lib/insights/constants/file-size-limit';
import type { InsightDocumentNode } from '@lib/insights/types/insight-node';
import { MAX_UPLOAD_SIZE_BYTES } from '@lib/shared/constants/max-upload-size';

type InsightUploadMime = (typeof ACCEPTED_MIME_TYPES)[number];

interface UploadInsightDocumentParams {
  file: File;
  parentSpaceNodeId: string | null;
}

interface BeginResponse {
  files: { documentId: string; uploadUrl: string; fileName: string }[];
}

const MIME_BY_EXTENSION: Record<
  (typeof ACCEPTED_FILE_EXTENSIONS)[number],
  InsightUploadMime
> = {
  '.pdf': 'application/pdf',
};

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new ApiError(await readApiErrorMessage(res), res.status);
  }

  return (await res.json()) as T;
}

/**
 * Turns a failed Storage PUT into something a user can act
 * on. Storage returns a JSON body naming the cause; falls
 * back to the status when it does not.
 *
 * A size rejection is the case that matters: it happens when
 * the bucket's `file_size_limit` is lower than
 * `MAX_UPLOAD_SIZE_BYTES`, which is what the client checked
 * against — so the two are out of step, and the message has
 * to say so rather than reporting a bare status code.
 */
async function readStorageUploadError(response: Response): Promise<string> {
  const fallback = `Upload failed (HTTP ${response.status}).`;
  try {
    const body = (await response.json()) as { message?: string };
    const message = body.message?.trim();
    if (!message) return fallback;
    return /size|large/i.test(message)
      ? `${message}. The file may exceed the server's upload limit.`
      : message;
  } catch {
    return fallback;
  }
}

function resolveInsightUploadMime(file: File): InsightUploadMime {
  if (
    ACCEPTED_MIME_TYPES.includes(
      file.type as (typeof ACCEPTED_MIME_TYPES)[number]
    )
  ) {
    return file.type as InsightUploadMime;
  }

  const lowerName = file.name.toLowerCase();
  for (const extension of ACCEPTED_FILE_EXTENSIONS) {
    if (lowerName.endsWith(extension)) {
      return MIME_BY_EXTENSION[extension];
    }
  }

  throw new Error('Only PDF files can be uploaded.');
}

export async function uploadInsightDocument({
  file,
  parentSpaceNodeId,
}: UploadInsightDocumentParams): Promise<InsightDocumentNode> {
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    throw new Error(fileSizeLimitMessage(file.size));
  }

  const contentType = resolveInsightUploadMime(file);
  const { files } = await postJson<BeginResponse>(
    '/api/protected/insights/documents/begin',
    {
      files: [{ name: file.name, size: file.size, contentType }],
    }
  );

  const target = files[0];
  if (!target) {
    throw new Error('Upload URL was not returned.');
  }

  // Re-typed Blob, not the File itself: the browser cannot
  // substitute `file.type` into Content-Type, which the
  // bucket allowlist rejects as application/octet-stream. A
  // Blob over a File references the bytes on disk, so the
  // PDF is streamed rather than read into memory first.
  const putRes = await fetch(target.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: new Blob([file], { type: contentType }),
  });

  if (!putRes.ok) {
    // Storage explains itself in the body — "exceeded the
    // maximum allowed size", "mime type not supported". The
    // status alone is not diagnosable: a size rejection here
    // reads as a bare 400, which is indistinguishable from a
    // bad signature. This is the one place that knows the
    // real reason, so surface it.
    throw new ApiError(await readStorageUploadError(putRes), putRes.status);
  }

  return postJson<InsightDocumentNode>('/api/protected/insights/documents', {
    parentSpaceNodeId,
    files: [{ documentId: target.documentId, name: target.fileName }],
  });
}
